/**
 * jbc-x3-configurator-modal.js
 * ----------------------------
 * Custom element for the X3 Configurator Modal.
 *
 * Features:
 * - Lazy loads configurator content via Section Rendering API
 * - Caches content after first load for instant subsequent opens
 * - Handles open/close with proper animations
 * - Listens for cart:update to auto-close after successful add
 * - Manages scroll lock and focus trap
 *
 * Usage:
 *   const modal = document.getElementById('x3-configurator-modal');
 *   modal.showDialog();  // Opens the modal
 *   modal.closeDialog(); // Closes the modal
 */

class X3ConfiguratorModal extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLDialogElement | null} */
    this.dialog = null;

    /** @type {HTMLElement | null} */
    this.contentEl = null;

    /** @type {HTMLElement | null} */
    this.closeBtn = null;

    /** @type {boolean} */
    this.isLoaded = false;

    /** @type {boolean} */
    this.isLoading = false;

    /** @type {AbortController | null} */
    this.abortController = null;

    // Bind methods
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleConfiguratorClose = this.closeDialog.bind(this);
    this.handleOpenTriggerClick = this.handleOpenTriggerClick.bind(this);
    this.handlePrefetchIntent = this.handlePrefetchIntent.bind(this);
    this.scheduleIdlePrefetch = this.scheduleIdlePrefetch.bind(this);
    this.schedulePostLoadPrefetch = this.schedulePostLoadPrefetch.bind(this);
    this.runPostLoadPrefetch = this.runPostLoadPrefetch.bind(this);

    /** @type {number | null} */
    this.idlePrefetchId = null;

    /** @type {number} */
    this.previousScrollY = 0;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.contentEl = this.querySelector('#x3-configurator-modal-content');
    this.closeBtn = this.querySelector('[data-close-modal]');

    if (!this.dialog) {
      console.error('[X3 Configurator Modal] Dialog element not found');
      return;
    }

    // Bind event listeners
    this.closeBtn?.addEventListener('click', this.handleCloseClick);
    this.dialog.addEventListener('click', this.handleBackdropClick);
    document.addEventListener('keydown', this.handleKeydown);

    // Listen for cart updates to auto-close
    document.addEventListener('cart:update', this.handleCartUpdate);

    // Listen for custom close event from configurator
    document.addEventListener('x3-configurator:close-modal', this.handleConfiguratorClose);

    // Support any page-level CTA with data-open-x3-configurator, not just the X3 mode selector.
    document.addEventListener('click', this.handleOpenTriggerClick);
    document.addEventListener('mouseover', this.handlePrefetchIntent);
    document.addEventListener('focusin', this.handlePrefetchIntent);
    document.addEventListener('touchstart', this.handlePrefetchIntent, { passive: true });

    this.schedulePostLoadPrefetch();
    this.scheduleIdlePrefetch();
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener('click', this.handleCloseClick);
    this.dialog?.removeEventListener('click', this.handleBackdropClick);
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    document.removeEventListener('x3-configurator:close-modal', this.handleConfiguratorClose);
    document.removeEventListener('click', this.handleOpenTriggerClick);
    document.removeEventListener('mouseover', this.handlePrefetchIntent);
    document.removeEventListener('focusin', this.handlePrefetchIntent);
    document.removeEventListener('touchstart', this.handlePrefetchIntent);
    document.removeEventListener('DOMContentLoaded', this.runPostLoadPrefetch);

    if (this.idlePrefetchId !== null) {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.idlePrefetchId);
      } else {
        window.clearTimeout(this.idlePrefetchId);
      }
      this.idlePrefetchId = null;
    }

    this.abortController?.abort();
  }

  /**
   * Opens the modal dialog.
   * Loads configurator content if not already loaded.
   */
  async showDialog() {
    if (!this.dialog || this.dialog.open) return;

    // Show the dialog first (for loading state)
    this.dialog.showModal();

    // showModal() auto-focuses the first focusable element (close button).
    // Safari treats this programmatic focus as :focus-visible and shows a ring.
    // Blurring immediately removes it without affecting keyboard navigation.
    this.closeBtn?.blur();

    // Lock body scroll — position:fixed removes body from scroll flow entirely
    const scrollY = window.scrollY;
    this.previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    });

    // Load configurator if not already loaded
    if (!this.isLoaded && !this.isLoading) {
      await this.loadConfigurator();
    }
  }

  /**
   * Closes the modal dialog with animation.
   */
  closeDialog() {
    if (!this.dialog || !this.dialog.open) return;

    // Add closing class for animation
    this.dialog.classList.add('dialog-closing');

    // Wait for animation to complete before closing
    const animationDuration = parseFloat(
      getComputedStyle(this.dialog).getPropertyValue('--animation-speed') || '0.3s'
    ) * 1000;

    setTimeout(() => {
      this.dialog.classList.remove('dialog-closing');
      this.dialog.close();

      // Restore body scroll and scroll position
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });
    }, animationDuration);
  }

  /**
   * Fetches and renders the configurator section.
   */
  async loadConfigurator() {
    if (!this.contentEl) return;

    this.isLoading = true;

    // Abort any previous request
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const configuratorUrl = this.dataset.configuratorUrl;
      const fallbackUrl = this.dataset.configuratorFallbackUrl;

      if (!configuratorUrl && !fallbackUrl) {
        throw new Error(
          '[X3 Configurator Modal] Missing configurator URL attributes.'
        );
      }

      let configuratorSection = null;
      let usedFallback = false;

      if (configuratorUrl) {
        try {
          configuratorSection = await this.fetchConfiguratorFromSectionURL(configuratorUrl);
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.warn('[X3 Configurator Modal] Section rendering fetch failed, falling back to page fetch.', error);
          } else {
            throw error;
          }
        }
      }

      if (!configuratorSection && fallbackUrl) {
        usedFallback = true;
        configuratorSection = await this.fetchConfiguratorFromPageURL(fallbackUrl);
      }

      if (!configuratorSection) {
        throw new Error(
          usedFallback
            ? 'Configurator section not found in fallback page response'
            : 'Configurator section not found in section rendering response'
        );
      }

      // Initialize the configurator
      this.initConfigurator(configuratorSection);
      if (this.hasAttribute('data-enable-sticky-bar')) {
        this.initStickyBar();
      }

      // If the page requested a preselect handle (set before opening), notify
      // the configurator instance so it can apply the selection even if the
      // configurator was prefetched earlier.
      try {
        const preselectHandle = this.dataset.preselectHandle;
        if (preselectHandle) {
          document.dispatchEvent(
            new CustomEvent('x3-configurator:preselect', { detail: { handle: preselectHandle } })
          );
        }
      } catch (_) {
        // Ignore preselect dispatch errors
      }

      this.isLoaded = true;
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }

      console.error('[X3 Configurator Modal] Error loading configurator:', error);

      // Show error message
      this.contentEl.innerHTML = `
        <div class="x3-configurator-modal__error">
          <p>Unable to load the configurator. Please try again.</p>
          <button type="button" class="button" onclick="document.getElementById('x3-configurator-modal').loadConfigurator()">
            Retry
          </button>
        </div>
      `;
    } finally {
      this.isLoading = false;
      this.abortController = null;
    }
  }

  async fetchHTML(url) {
    const response = await fetch(url, {
      signal: this.abortController.signal,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to load configurator: ${response.status}`);
    }

    return response.text();
  }

  async fetchConfiguratorFromSectionURL(url) {
    const html = await this.fetchHTML(url);

    this.contentEl.innerHTML = html;

    const section = this.contentEl.querySelector('.x3-configurator[data-configurator]');
    if (section) {
      section.removeAttribute('data-configurator-initialized');
    }

    return section;
  }

  async fetchConfiguratorFromPageURL(url) {
    const html = await this.fetchHTML(url);

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const configuratorSection = doc.querySelector('.section-x3-configurator');

    if (!configuratorSection) return null;

    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(configuratorSection);

    const section = this.contentEl.querySelector('.x3-configurator[data-configurator]');
    if (section) {
      section.removeAttribute('data-configurator-initialized');
    }

    return section;
  }

  prefetchConfigurator() {
    if (this.isLoaded || this.isLoading) return;
    this.loadConfigurator();
  }

  /**
   * Initializes the X3Configurator class for the newly loaded DOM.
   * The configurator script is loaded via <script defer> in the modal snippet,
   * so it should always be available by the time this method is called.
   */
  initConfigurator(configuratorSection) {
    const rootSection = configuratorSection?.querySelector('.x3-configurator[data-configurator]') || configuratorSection;
    if (rootSection) {
      rootSection.removeAttribute('data-configurator-initialized');
    }

    if (typeof window.initX3Configurators === 'function') {
      window.initX3Configurators();
    } else if (typeof window.X3Configurator === 'function') {
      new window.X3Configurator(rootSection || undefined);
    } else {
      throw new Error(
        '[X3 Configurator Modal] X3Configurator class not found. ' +
        'Ensure jbc-x3-configurator.js is loaded via a <script defer> tag.'
      );
    }
  }

  initStickyBar() {
    const scrollContainer = this.contentEl;
    const nativeAtcBtn = scrollContainer.querySelector('[data-configurator-add-to-cart]');
    const totalPriceEl = scrollContainer.querySelector('[data-configurator-total-price]');

    if (!nativeAtcBtn || !totalPriceEl) return;

    const bar = document.createElement('div');
    bar.className = 'x3-configurator-sticky-bar';
    bar.setAttribute('aria-hidden', 'true');
    const nativeAtcText = nativeAtcBtn.querySelector('.x3-configurator__add-to-cart-text');
    const initialAtcLabel = nativeAtcText?.textContent?.trim() || 'Add to Cart';
    bar.innerHTML = `
      <div class="x3-configurator-sticky-bar__summary">
        <span class="x3-configurator-sticky-bar__label">Your X3</span>
        <span class="x3-configurator-sticky-bar__price">${totalPriceEl.textContent}</span>
      </div>
      <button type="button" class="x3-configurator__add-to-cart x3-configurator-sticky-bar__atc">
        <span class="x3-configurator__add-to-cart-text">${initialAtcLabel}</span>
      </button>
    `;
    scrollContainer.prepend(bar);

    const stickyPriceEl = bar.querySelector('.x3-configurator-sticky-bar__price');
    const stickyAtcBtn = bar.querySelector('.x3-configurator-sticky-bar__atc');

    const observer = new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle('x3-configurator-sticky-bar--visible', !entry.isIntersecting);
      },
      { root: scrollContainer, threshold: 0 }
    );
    observer.observe(nativeAtcBtn);

    const priceObserver = new MutationObserver(() => {
      stickyPriceEl.textContent = totalPriceEl.textContent;
    });
    priceObserver.observe(totalPriceEl, { characterData: true, childList: true, subtree: true });

    // Keep sticky button label in sync with the native button label
    const stickyAtcTextEl = stickyAtcBtn.querySelector('.x3-configurator__add-to-cart-text');
    if (nativeAtcText && stickyAtcTextEl) {
      const atcTextObserver = new MutationObserver(() => {
        stickyAtcTextEl.textContent = nativeAtcText.textContent;
      });
      atcTextObserver.observe(nativeAtcText, { characterData: true, childList: true, subtree: true });
    }

    // Hide sticky button entirely when belt selection is required (--blocked)
    const syncStickyAtcVisibility = () => {
      const isBlocked = nativeAtcBtn.classList.contains('x3-configurator__add-to-cart--blocked');
      stickyAtcBtn.style.display = isBlocked ? 'none' : '';
      stickyAtcBtn.disabled = nativeAtcBtn.disabled;
    };
    syncStickyAtcVisibility();
    const atcStateObserver = new MutationObserver(syncStickyAtcVisibility);
    atcStateObserver.observe(nativeAtcBtn, { attributes: true, attributeFilter: ['class', 'disabled'] });

    stickyAtcBtn.addEventListener('click', () => nativeAtcBtn.click());
  }

  /**
   * Handles close button click.
   * @param {Event} event
   */
  handleCloseClick(event) {
    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Handles click on the backdrop (outside dialog content).
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    // Only close if clicking directly on the dialog (backdrop)
    if (event.target === this.dialog) {
      this.closeDialog();
    }
  }

  /**
   * Handles keydown events (Escape to close).
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape' && this.dialog?.open) {
      event.preventDefault();
      this.closeDialog();
    }
  }

  /**
   * Handles cart:update event to auto-close modal.
   * @param {CustomEvent} event
   */
  handleCartUpdate(event) {
    // Check if the update came from the configurator
    const source = event.detail?.sourceId || event.detail?.data?.source;
    if (source === 'x3-configurator' && this.dialog?.open) {
      // Small delay to allow cart drawer to open
      setTimeout(() => {
        this.closeDialog();
      }, 100);
    }
  }

  /**
   * Opens the modal from any CTA carrying the shared configurator trigger attribute.
   * @param {MouseEvent} event
   */
  handleOpenTriggerClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-open-x3-configurator]');
    if (!trigger) return;

    event.preventDefault();
    const parts = window.location.pathname.split('/').filter(Boolean);
    const handle = parts[parts.length - 1] || '';
    if (handle) {
      this.dataset.preselectHandle = handle;
      try {
        document.dispatchEvent(new CustomEvent('x3-configurator:preselect', { detail: { handle } }));
      } catch (_) {
        // Ignore preselect dispatch errors
      }
    }

    // Cart edit context — passed when opening from a cart line item link
    const cartItemKey = trigger.dataset.x3CartItemKey || null;
    const cartVariantId = trigger.dataset.x3CartVariantId || null;
    const cartBeltVariantId = trigger.dataset.x3CartBeltVariantId || null;
    const cartCompanionItems = trigger.dataset.x3CartCompanionItems || null;
    const configurationId = trigger.dataset.x3ConfigurationId || null;
    if (cartItemKey) {
      this.dataset.x3CartItemKey = cartItemKey;
      this.dataset.x3CartVariantId = cartVariantId;
      if (configurationId) {
        this.dataset.x3ConfigurationId = configurationId;
      } else {
        delete this.dataset.x3ConfigurationId;
      }
      if (cartBeltVariantId) {
        this.dataset.x3CartBeltVariantId = cartBeltVariantId;
      } else {
        delete this.dataset.x3CartBeltVariantId;
      }
      if (cartCompanionItems) {
        this.dataset.x3CartCompanionItems = cartCompanionItems;
      } else {
        delete this.dataset.x3CartCompanionItems;
      }
    } else {
      delete this.dataset.x3CartItemKey;
      delete this.dataset.x3CartVariantId;
      delete this.dataset.x3CartBeltVariantId;
      delete this.dataset.x3CartCompanionItems;
      delete this.dataset.x3ConfigurationId;
    }
    try {
      document.dispatchEvent(new CustomEvent('x3-configurator:edit-cart-item', {
        detail: {
          cartItemKey,
          variantId: cartVariantId,
          beltVariantId: cartBeltVariantId,
          companionItems: cartCompanionItems,
          configurationId,
        },
      }));
    } catch (_) {
      // Ignore dispatch errors
    }

    this.showDialog();
  }

  /**
   * Warms the configurator fetch when a user signals intent on a CTA.
   * @param {Event} event
   */
  handlePrefetchIntent(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-open-x3-configurator]');
    if (!trigger) return;

    this.prefetchConfigurator();
  }

  scheduleIdlePrefetch() {
    const hasTrigger = document.querySelector('[data-open-x3-configurator]');
    if (!hasTrigger || this.isLoaded || this.isLoading) return;

    const callback = () => {
      this.idlePrefetchId = null;
      this.prefetchConfigurator();
    };

    if (typeof window.requestIdleCallback === 'function') {
      this.idlePrefetchId = window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      this.idlePrefetchId = window.setTimeout(callback, 1500);
    }
  }

  runPostLoadPrefetch() {
    document.removeEventListener('DOMContentLoaded', this.runPostLoadPrefetch);
    this.prefetchConfigurator();
  }

  schedulePostLoadPrefetch() {
    const hasTrigger = document.querySelector('[data-open-x3-configurator]');
    if (!hasTrigger || this.isLoaded || this.isLoading) return;

    // Fire at DOMContentLoaded (readyState 'interactive' or 'complete') rather
    // than window.load + 600ms — the DOM is ready and page-critical resources
    // are not yet competing, but we're much earlier in the timeline.
    if (document.readyState !== 'loading') {
      this.runPostLoadPrefetch();
    } else {
      document.addEventListener('DOMContentLoaded', this.runPostLoadPrefetch, { once: true });
    }
  }
}

// Register the custom element
if (!customElements.get('x3-configurator-modal')) {
  customElements.define('x3-configurator-modal', X3ConfiguratorModal);
}

// Export for potential external use
window.X3ConfiguratorModal = X3ConfiguratorModal;
